import React from 'react';
import { FilterOptions } from '../types';
import { workshops, districts, getImplementationPartners } from '../utils/analytics';
import { X, Calendar, MapPin, Users, BookOpen, Filter } from 'lucide-react';
import MultiSelectDropdown from './MultiSelectDropdown';

interface FiltersProps {
    filters: FilterOptions;
    onFiltersChange: (filters: FilterOptions) => void;
    showResetButton?: boolean;
    footer?: React.ReactNode;
}

const Filters: React.FC<FiltersProps> = ({ filters, onFiltersChange, showResetButton = true, footer }) => {
    const implementationPartners = getImplementationPartners();

    const updateFilters = (key: keyof FilterOptions, value: any) => {
        onFiltersChange({
            ...filters,
            [key]: value
        });
    };

    const clearAllFilters = () => {
        onFiltersChange({
            workshops: [],
            districts: [],
            implementationPartners: [],
            dateRange: { start: '', end: '' }
        });
    };

    const hasActiveFilters =
        filters.workshops.length > 0 ||
        filters.districts.length > 0 ||
        filters.implementationPartners.length > 0 ||
        filters.dateRange.start ||
        filters.dateRange.end;

    return (
        <div className="bg-white rounded-xl shadow-sm p-4 border border-slate-200 transition-all duration-300 hover:shadow-md">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-2 mb-3">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                        <Filter size={18} className="text-slate-600" />
                    </div>
                    <h2 className="text-lg font-semibold text-slate-700">Filters</h2>
                </div>
                {showResetButton && hasActiveFilters && (
                    <button
                        onClick={clearAllFilters}
                        className="text-sm text-blue-600 hover:text-blue-800 font-semibold transition-colors w-full sm:w-auto px-4 py-2 rounded-lg hover:bg-blue-50 flex items-center gap-2"
                    >
                        <X className="w-4 h-4" />
                        Reset Filters
                    </button>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Date Range Filter */}
                <div className="space-y-2">
                    <label className="flex text-sm font-medium text-slate-700 items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Date Range (Workshop End Date)
                    </label>
                    <div className="space-y-2">
                        <input
                            type="date"
                            value={filters.dateRange.start}
                            onChange={(e) => updateFilters('dateRange', { ...filters.dateRange, start: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="Start Date"
                        />
                        <input
                            type="date"
                            value={filters.dateRange.end}
                            onChange={(e) => updateFilters('dateRange', { ...filters.dateRange, end: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                            placeholder="End Date"
                        />
                    </div>
                </div>

                {/* Workshop Filter */}
                <MultiSelectDropdown
                    label="Workshops"
                    icon={<BookOpen className="w-4 h-4" />}
                    options={workshops}
                    selectedValues={filters.workshops}
                    onSelectionChange={(selectedValues) => updateFilters('workshops', selectedValues)}
                    placeholder="Select workshops..."
                />

                {/* District Filter */}
                <MultiSelectDropdown
                    label="Districts"
                    icon={<MapPin className="w-4 h-4" />}
                    options={districts.map(district => ({ id: district, name: district }))}
                    selectedValues={filters.districts}
                    onSelectionChange={(selectedValues) => updateFilters('districts', selectedValues)}
                    placeholder="Select districts..."
                />

                {/* Implementation Partner Filter */}
                <MultiSelectDropdown
                    label="Implementation Partners"
                    icon={<Users className="w-4 h-4" />}
                    options={implementationPartners}
                    selectedValues={filters.implementationPartners}
                    onSelectionChange={(selectedValues) => updateFilters('implementationPartners', selectedValues)}
                    placeholder="Select partners..."
                />
            </div>

            {footer && <div className="mt-3">{footer}</div>}
        </div>
    );
};

export default Filters;

